// S-02b - Référentiels Admin (rôles, populations, langues, domaines, départements, pays)
import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { referentialsApi, ApiRequestError, type RefItemDto } from "../api/api";
import { PageHeader } from "../components/ui/PageHeader";
import { localeLabel } from "../utils/locale";

type Kind = "roles" | "populations" | "languages" | "domains" | "departments" | "countries";

const KINDS: Kind[] = [
  "departments",
  "domains",
  "countries",
  "roles",
  "populations",
  "languages",
];

const PLAQUES = ["EMEA", "AMAS", "APOC", "GC"] as const;

const emptyForm = {
  code: "",
  labelEn: "",
  labelFr: "",
  accessLevel: 10,
  departmentId: "",
  plaque: "EMEA",
};

export function ReferentialsPage() {
  const { t, i18n } = useTranslation();
  const [kind, setKind] = useState<Kind>("departments");
  const [items, setItems] = useState<RefItemDto[]>([]);
  const [departments, setDepartments] = useState<RefItemDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importReport, setImportReport] = useState<{
    created: number;
    updated: number;
    errors: Array<{ row: number; message: string }>;
  } | null>(null);
  const [busy, setBusy] = useState(false);

  async function load(k = kind) {
    setError(null);
    setLoading(true);
    setItems([]);
    try {
      const res = await referentialsApi.list(k);
      setItems(res.items);
      if (k === "domains") {
        const deptRes = await referentialsApi.list("departments");
        setDepartments(deptRes.items.filter((d) => d.active));
      }
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("load_failed"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setEditingId(null);
    setForm(emptyForm);
    setImportFile(null);
    setImportReport(null);
    void load(kind);
  }, [kind]);

  async function downloadTemplate() {
    try {
      const blob = await referentialsApi.downloadTemplate(kind);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `skillmap-${kind}-template.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("download_failed"));
    }
  }

  async function onImport(event: FormEvent) {
    event.preventDefault();
    if (!importFile) {
      setError(t("choose_xlsx"));
      return;
    }
    setBusy(true);
    setError(null);
    setImportReport(null);
    try {
      const report = await referentialsApi.importBulk(kind, importFile);
      setImportReport(report);
      setImportFile(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("import_failed"));
    } finally {
      setBusy(false);
    }
  }

  function startEdit(item: RefItemDto) {
    setEditingId(item.id);
    setError(null);
    setForm({
      code: item.code,
      labelEn: item.labelEn,
      labelFr: item.labelFr,
      accessLevel: item.accessLevel ?? 10,
      departmentId: item.departmentId ?? "",
      plaque: item.plaque ?? "EMEA",
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(emptyForm);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      if (editingId) {
        await referentialsApi.patch(kind, editingId, {
          labelEn: form.labelEn.trim(),
          labelFr: form.labelFr.trim(),
          accessLevel: kind === "roles" ? form.accessLevel : undefined,
          departmentId: kind === "domains" ? form.departmentId || undefined : undefined,
          plaque: kind === "countries" ? form.plaque : undefined,
        });
        setEditingId(null);
      } else {
        await referentialsApi.create(kind, {
          code: form.code.trim(),
          labelEn: form.labelEn.trim(),
          labelFr: form.labelFr.trim(),
          accessLevel: kind === "roles" ? form.accessLevel : undefined,
          departmentId: kind === "domains" ? form.departmentId || undefined : undefined,
          plaque: kind === "countries" ? form.plaque : undefined,
        });
      }
      setForm(emptyForm);
      await load();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("save_failed"));
    }
  }

  async function toggleActive(item: RefItemDto) {
    setError(null);
    try {
      await referentialsApi.patch(kind, item.id, { active: !item.active });
      await load();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("update_failed"));
    }
  }

  return (
    <div className="sm-page">
      <PageHeader title={t("referentials")} subtitle={t("referentials_sub")} />

      <div className="sm-tabs">
        {KINDS.map((k) => (
          <button
            key={k}
            type="button"
            className={`sm-tab${kind === k ? " active" : ""}`}
            onClick={() => setKind(k)}
          >
            {t(`ref_kind_${k}`)}
          </button>
        ))}
      </div>

      {error ? (
        <div className="sm-alert sm-alert-error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="sm-form-card" style={{ marginBottom: 16 }}>
        <h2>{t("import_excel_kind", { kind: t(`ref_kind_${kind}`) })}</h2>
        <button type="button" className="sm-btn sm-btn-secondary" style={{ marginBottom: 8 }} onClick={() => void downloadTemplate()}>
          ⬇ {t("download_excel_template")}
        </button>
        <p className="sm-muted" style={{ marginBottom: 8 }}>
          {t("import_excel_templates")}
        </p>
        <form onSubmit={onImport}>
          <div className="sm-form-actions">
            <input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => setImportFile(e.target.files?.[0] ?? null)}
              disabled={busy}
            />
            <button type="submit" className="sm-btn sm-btn-primary" disabled={busy}>
              {t("import")}
            </button>
          </div>
        </form>
        {importReport ? (
          <div style={{ marginTop: 8 }}>
            <p className="sm-muted">
              {t("created_n")} : <strong>{importReport.created}</strong> — {t("updated_n")} :{" "}
              <strong>{importReport.updated}</strong> — {t("errors_n")} :{" "}
              <strong>{importReport.errors.length}</strong>
            </p>
            {importReport.errors.length > 0 ? (
              <ul className="sm-alert sm-alert-error" style={{ paddingLeft: 24 }}>
                {importReport.errors.map((e) => (
                  <li key={`${e.row}-${e.message}`}>
                    {t("excel_row", { row: e.row })} {e.message}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="sm-card" style={{ marginBottom: 16 }}>
        <div className="sm-table-wrap">
          <table className="sm-table">
            <thead>
              <tr>
                <th>{t("code")}</th>
                <th>{t("label_en")}</th>
                <th>{t("label_fr")}</th>
                {kind === "roles" ? <th>{t("access_level")}</th> : null}
                {kind === "domains" ? <th>{t("department")}</th> : null}
                {kind === "countries" ? <th>{t("plaque")}</th> : null}
                <th>{t("active")}</th>
                <th>{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="sm-muted" style={{ textAlign: "center", padding: 24 }}>
                    {t("loading")}
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="sm-muted" style={{ textAlign: "center", padding: 24 }}>
                    {t("no_entries")}
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id} className={editingId === item.id ? "sm-row-editing" : undefined}>
                    <td>
                      {item.code}
                      {item.isSystem ? " *" : ""}
                    </td>
                    <td>{item.labelEn}</td>
                    <td>{item.labelFr}</td>
                    {kind === "roles" ? <td>{item.accessLevel}</td> : null}
                    {kind === "domains" ? <td>{item.departmentCode ?? "—"}</td> : null}
                    {kind === "countries" ? (
                      <td>
                        <span className="sm-badge-pill sm-badge-blue">{item.plaque ?? "—"}</span>
                      </td>
                    ) : null}
                    <td>
                      <span className={`sm-badge-pill ${item.active ? "sm-badge-green" : "sm-badge-red"}`}>
                        {item.active ? t("status_active") : t("status_inactive")}
                      </span>
                    </td>
                    <td>
                      <div className="actions">
                        <button type="button" className="sm-link" onClick={() => startEdit(item)}>
                          {t("edit")}
                        </button>
                        <button type="button" className="sm-link" onClick={() => void toggleActive(item)}>
                          {item.active ? t("deactivate") : t("activate")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <p className="sm-muted">{t("system_entry_hint")}</p>

      <form onSubmit={onSubmit} className="sm-form-card" style={{ maxWidth: 900 }}>
        <h2>
          {editingId
            ? t("edit_code", { code: form.code })
            : t("add_kind", { kind: t(`ref_kind_${kind}`) })}
        </h2>
        <div className="sm-form-actions">
          <input
            required
            className="sm-input"
            placeholder={kind === "countries" ? "FR" : "CODE"}
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            style={{ textTransform: "uppercase" }}
            disabled={!!editingId}
            title={editingId ? t("code_immutable") : undefined}
          />
          <input
            required
            className="sm-input"
            placeholder={t("label_en")}
            value={form.labelEn}
            onChange={(e) => setForm({ ...form, labelEn: e.target.value })}
          />
          <input
            required
            className="sm-input"
            placeholder={t("label_fr")}
            value={form.labelFr}
            onChange={(e) => setForm({ ...form, labelFr: e.target.value })}
          />
          {kind === "domains" ? (
            <select
              required
              className="sm-input"
              value={form.departmentId}
              onChange={(e) => setForm({ ...form, departmentId: e.target.value })}
              title={t("parent_department")}
            >
              <option value="">{t("parent_department_opt")}</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.code} — {localeLabel(i18n.language, d)}
                </option>
              ))}
            </select>
          ) : null}
          {kind === "countries" ? (
            <select
              required
              className="sm-input"
              value={form.plaque}
              onChange={(e) => setForm({ ...form, plaque: e.target.value })}
              title={t("geo_plaque")}
            >
              {PLAQUES.map((p) => (
                <option key={p} value={p}>
                  {p}
                  {p === "GC" ? " (Greater China)" : p === "APOC" ? " (Asia Pacific)" : ""}
                </option>
              ))}
            </select>
          ) : null}
          {kind === "roles" ? (
            <input
              type="number"
              className="sm-input"
              min={0}
              max={1000}
              title={t("access_level_hint")}
              value={form.accessLevel}
              onChange={(e) => setForm({ ...form, accessLevel: Number(e.target.value) })}
              style={{ maxWidth: 100 }}
            />
          ) : null}
          <button type="submit" className="sm-btn sm-btn-primary">
            {editingId ? t("save") : t("add")}
          </button>
          {editingId ? (
            <button type="button" className="sm-btn sm-btn-ghost" onClick={cancelEdit}>
              {t("cancel")}
            </button>
          ) : null}
        </div>
      </form>
    </div>
  );
}
